
import { useEffect, useState } from 'react';
import axios from 'axios';
import {  useSearchParams } from 'react-router-dom';
import { setHyperDXContext } from '../utils/hyperdx';


const useSessionId = (
    journeyId,
    setJourneyInitialized,
    setJourneyTerminated,
    setJourneyExpired,
    setSessionExpired,
    setJourneyDone,
    setIsInvalidJourneyId,
    setIsDirectKycJourney,
    setError
) => {
    const url=window.location.href;
    const urlParts=url.split(/[/?]/);
    const journeyIdFromParams=urlParts.length >=4 ? urlParts[3]:"";
    const queryParams = useSearchParams()[0];
    const sessionIdFromParams = queryParams.get('sessionId');
    const [sessionId, setSessionId] = useState(() =>
        sessionIdFromParams ?? sessionStorage.getItem('sessionId')
    );
    const [internalError, setInternalError] = useState(null);

    // Persist the handoff session during render so jId exists before the first effect reads it; skipped when another journey is stored so its cleanup still runs.
    if (sessionIdFromParams && journeyIdFromParams && !sessionStorage.getItem('jId')) {
        sessionStorage.setItem('sessionId', sessionIdFromParams);
        sessionStorage.setItem('jId', journeyIdFromParams);
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (internalError) setInternalError(null);
                if (setError) setError(null);
                const storedSessionId = sessionIdFromParams ?? sessionStorage.getItem('sessionId') ;
                const jId = journeyIdFromParams ?? sessionStorage.getItem('jId');
                const storedJourneyId = sessionStorage.getItem('jId');

                // If journeyId has changed, immediately clear old session data and reset sessionId.
                // Guard on storedJourneyId: after completion the isJourneyDone handler clears jId,
                // so on a later reload storedJourneyId is null while the URL still carries the same
                // journeyId. Without this guard that reload is mistaken for a journey switch and wipes
                // language + biometricDeclined, reverting the terminal page to English and dropping the
                // decline state (SA-33713). Only wipe on a genuine switch to a DIFFERENT journey.
                if (journeyId && storedJourneyId && journeyId !== storedJourneyId) {
                    sessionStorage.removeItem('sessionId');
                    sessionStorage.removeItem('jId');
                    sessionStorage.removeItem('companyLogo');
                    sessionStorage.removeItem('currentStep');
                    sessionStorage.removeItem('language');
                    sessionStorage.removeItem('routesAndNavigation');
                    sessionStorage.removeItem('currentDocumentStep');
                    sessionStorage.removeItem('currentDocumentRetries');
                    sessionStorage.removeItem('documentFailureReason');
                    sessionStorage.removeItem('errorCategory');
                    sessionStorage.removeItem('errorReason');
                    sessionStorage.removeItem('error');
                    sessionStorage.removeItem('document');
                    sessionStorage.removeItem('firstVerifiedDocumentName');
                    sessionStorage.removeItem('verifiedDocumentNames');
                    sessionStorage.removeItem('verifiedDocuments');
                    sessionStorage.removeItem('selectedCountry');
                    sessionStorage.removeItem('backCameraDeviceId');
                    sessionStorage.removeItem('isCameraPage');
                    sessionStorage.removeItem('isPostReload');
                    sessionStorage.removeItem('nonImportantStep');
                    sessionStorage.removeItem('digilockerUrl');
                    sessionStorage.removeItem('userDetails');
                    sessionStorage.removeItem('biometricDeclined');
                    // Reset sessionId state to null to prevent using old session
                    setSessionId(null);
                }
    
                // Only proceed if we have a valid journeyId
                const currentJourneyId = journeyId ?? jId;
                if (!currentJourneyId) {
                    return;
                }

                // Check if we already have a session for this specific journey
                const currentStoredSessionId = sessionStorage.getItem('sessionId');
                const currentStoredJourneyId = sessionStorage.getItem('jId');
                
                if (currentStoredSessionId && currentStoredJourneyId === currentJourneyId) {
                    setSessionId(currentStoredSessionId);
                    return;
                }

                const { data: { result } } = await axios.post(
                    '/otk-service/create-session',
                    { journeyId: currentJourneyId }
                );
                if (result?.sessionId) {
                    sessionStorage.setItem('sessionId', result.sessionId);
                    sessionStorage.setItem('jId', currentJourneyId);
                    setSessionId(result.sessionId);
                    // Correlate HyperDX telemetry with this journey/session.
                    setHyperDXContext({
                        journeyId: currentJourneyId,
                        sessionId: result.sessionId,
                    });
                }
                if(result?.isJourneyInitialized){
                    setJourneyInitialized(1);
                }
                if (result?.isJourneyDone) {
                    sessionStorage.removeItem("sessionId");
                    sessionStorage.removeItem("jId");
                    sessionStorage.removeItem("routesAndNavigation"); // Clean up routes and navigation
                    if (setJourneyTerminated && (result?.journeyDoneReason == "RETRIES_EXCEEDED" || result?.journeyDoneReason == "DUPLICATE_FOUND")) {
                        // Dedup TERMINATE: show the backend-provided message on the terminate page
                        // instead of the frontend's default (retries) copy.
                        if (result?.journeyDoneReason == "DUPLICATE_FOUND" && result?.message) {
                            sessionStorage.setItem("terminationMessage", result.message);
                        }
                        setJourneyTerminated(true);
                    } else if (setJourneyExpired && result?.journeyDoneReason == "JOURNEY_EXPIRED") {
                        setJourneyExpired(true);
                    } else if (setSessionExpired && result?.journeyDoneReason == "SESSION_TIMED_OUT") {
                        setSessionExpired(true);
                    } else if (setJourneyDone) {
                        setJourneyDone(true);
                    }
                }
            } catch (error) {
                if (error.response?.data?.error?.error?.statusCode == 403) {
                    sessionStorage.removeItem("sessionId");
                    sessionStorage.removeItem("jId");
                    sessionStorage.removeItem("routesAndNavigation"); // Clean up routes and navigation
                    if (setIsDirectKycJourney && error?.response?.data?.error?.error?.reason == "DIRECT_KYC_JOURNEY_UPDATE_REQUEST_BLOCKED") {
                        setIsDirectKycJourney(true);
                    } else if (
                        setJourneyExpired &&
                        typeof error.response?.data?.error?.error?.error?.message === "string" &&
                        error.response?.data?.error?.error?.error?.message.includes("Journey")) {
                        setJourneyExpired(true);
                    } else if (setSessionExpired) {
                        setSessionExpired(true);
                    }
                    if (setError) {
                        setError(error);
                    }
                } else if (
                    setIsInvalidJourneyId &&
                    typeof error.response?.data?.error?.error?.message === "string" && 
                    error.response?.data?.error?.error?.message.includes("Invalid journeyId")) {
                    setIsInvalidJourneyId(true);
                } else {
                    sessionStorage.setItem("error", true);
                }
                setInternalError(error.message);
            }
        };

        if (journeyId || journeyIdFromParams) {
            fetchData();
        }
    }, [journeyId, journeyIdFromParams, sessionIdFromParams]);


    return [sessionId, internalError];
};

export default useSessionId;
